'use client';

import { useSession } from 'next-auth/react';
import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { toast } from 'src/utils/use-toast';
import { apiFetch } from 'src/utils/api';

export interface PropertyWithStatus {
  id: string | number;
  is_liked?: boolean;
  is_wishlisted?: boolean;
}

type PropertyInteractionPatch = {
  isLiked?: boolean;
  isWishlisted?: boolean;
};

const propertyInteractionStore = new Map<string, PropertyInteractionPatch>();
const propertyInteractionListeners = new Set<() => void>();
let propertyInteractionVersion = 0;
let propertyInteractionOwner = 'anonymous';

const subscribeToPropertyInteractions = (listener: () => void) => {
  propertyInteractionListeners.add(listener);
  return () => propertyInteractionListeners.delete(listener);
};

const getPropertyInteractionSnapshot = () => propertyInteractionVersion;

const emitPropertyInteractionChange = () => {
  propertyInteractionVersion += 1;
  propertyInteractionListeners.forEach((listener) => listener());
};

const normalizePropertyId = (propertyId: string | number | undefined) => {
  if (propertyId === undefined || propertyId === null || propertyId === '') {
    return undefined;
  }
  return String(propertyId);
};

const ensurePropertyInteractionOwner = (nextOwner: string) => {
  if (propertyInteractionOwner === nextOwner) return;
  propertyInteractionOwner = nextOwner;
  propertyInteractionStore.clear();
  emitPropertyInteractionChange();
};

const resolvePropertyInteractionState = (
  propertyId: string | undefined,
  fallback: PropertyInteractionPatch = {},
) => {
  const stored = propertyId
    ? propertyInteractionStore.get(propertyId)
    : undefined;

  return {
    isLiked: stored?.isLiked ?? fallback.isLiked ?? false,
    isWishlisted: stored?.isWishlisted ?? fallback.isWishlisted ?? false,
  };
};

const seedPropertyInteractionState = (
  propertyId: string | undefined,
  patch: PropertyInteractionPatch,
) => {
  if (!propertyId) return false;

  const previous = propertyInteractionStore.get(propertyId) ?? {};
  const next = { ...previous };
  let changed = false;

  if (patch.isLiked !== undefined && previous.isLiked === undefined) {
    next.isLiked = patch.isLiked;
    changed = true;
  }

  if (patch.isWishlisted !== undefined && previous.isWishlisted === undefined) {
    next.isWishlisted = patch.isWishlisted;
    changed = true;
  }

  if (changed) {
    propertyInteractionStore.set(propertyId, next);
  }

  return changed;
};

const setPropertyInteractionState = (
  propertyId: string | undefined,
  patch: PropertyInteractionPatch,
) => {
  if (!propertyId) return;

  const previous = propertyInteractionStore.get(propertyId) ?? {};
  const next = { ...previous, ...patch };

  if (
    previous.isLiked === next.isLiked &&
    previous.isWishlisted === next.isWishlisted
  ) {
    return;
  }

  propertyInteractionStore.set(propertyId, next);
  emitPropertyInteractionChange();
};

const usePropertyInteractionOwner = (ownerKey: string) => {
  useEffect(() => {
    ensurePropertyInteractionOwner(ownerKey);
  }, [ownerKey]);
};

const useSeedPropertyInteraction = (
  propertyId: string | undefined,
  patch: PropertyInteractionPatch,
) => {
  useEffect(() => {
    if (seedPropertyInteractionState(propertyId, patch)) {
      emitPropertyInteractionChange();
    }
  }, [propertyId, patch.isLiked, patch.isWishlisted]);
};

interface UsePropertyActionsOptions {
  /** Initial liked state from the property data */
  initialIsLiked?: boolean;
  /** Initial wishlisted state from the property data */
  initialIsWishlisted?: boolean;
}

interface UsePropertyActionsReturn {
  isLiked: boolean;
  isWishlisted: boolean;
  isLoading: boolean;
  toggleLike: () => Promise<void>;
  toggleWishlist: () => Promise<void>;
  isAuthenticated: boolean;
}

/**
 * Hook for managing property like and wishlist actions via API
 * @param propertyId - The property ID to manage
 * @param options - Optional initial values from the property data
 */
export function usePropertyActions(
  propertyId: string | undefined,
  options?: UsePropertyActionsOptions,
): UsePropertyActionsReturn {
  const { data: session, status } = useSession();
  const [isLoading, setIsLoading] = useState(false);

  const isAuthenticated = status === 'authenticated';
  const accessToken = session?.user?.accessToken;
  const ownerKey = session?.user?.email || accessToken || 'anonymous';
  const normalizedPropertyId = normalizePropertyId(propertyId);

  usePropertyInteractionOwner(ownerKey);
  useSyncExternalStore(
    subscribeToPropertyInteractions,
    getPropertyInteractionSnapshot,
    getPropertyInteractionSnapshot,
  );
  useSeedPropertyInteraction(normalizedPropertyId, {
    isLiked: options?.initialIsLiked,
    isWishlisted: options?.initialIsWishlisted,
  });

  const { isLiked, isWishlisted } = resolvePropertyInteractionState(
    normalizedPropertyId,
    {
      isLiked: options?.initialIsLiked,
      isWishlisted: options?.initialIsWishlisted,
    },
  );

  const toggleLike = useCallback(async () => {
    if (!normalizedPropertyId) return;

    if (!isAuthenticated || !accessToken) {
      toast({
        title: 'Please log in',
        description: 'You need to be logged in to like properties',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    const wasLiked = isLiked;

    // Optimistic update
    setPropertyInteractionState(normalizedPropertyId, { isLiked: !wasLiked });

    try {
      const response = await apiFetch(
        `/properties/${encodeURIComponent(normalizedPropertyId)}/like`,
        accessToken,
        {
          method: wasLiked ? 'DELETE' : 'POST',
        },
      );

      if (!response.ok) {
        // Revert optimistic update
        setPropertyInteractionState(normalizedPropertyId, {
          isLiked: wasLiked,
        });
        const errorData = await response.json().catch(() => ({}));
        toast({
          title: 'Error',
          description: errorData.detail || 'Failed to update like status',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: wasLiked ? 'Removed from likes' : 'Added to likes',
        variant: 'default',
      });
    } catch (error: any) {
      // Revert optimistic update
      setPropertyInteractionState(normalizedPropertyId, { isLiked: wasLiked });
      toast({
        title: 'Error',
        description: error.message || 'Failed to update like status',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [normalizedPropertyId, isLiked, isAuthenticated, accessToken]);

  const toggleWishlist = useCallback(async () => {
    if (!normalizedPropertyId) return;

    if (!isAuthenticated || !accessToken) {
      toast({
        title: 'Please log in',
        description: 'You need to be logged in to add properties to wishlist',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    const wasWishlisted = isWishlisted;

    // Optimistic update
    setPropertyInteractionState(normalizedPropertyId, {
      isWishlisted: !wasWishlisted,
    });

    try {
      const response = await apiFetch(
        `/properties/${encodeURIComponent(normalizedPropertyId)}/wishlist`,
        accessToken,
        {
          method: wasWishlisted ? 'DELETE' : 'POST',
        },
      );

      if (!response.ok) {
        // Revert optimistic update
        setPropertyInteractionState(normalizedPropertyId, {
          isWishlisted: wasWishlisted,
        });
        const errorData = await response.json().catch(() => ({}));
        toast({
          title: 'Error',
          description: errorData.detail || 'Failed to update wishlist',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: wasWishlisted ? 'Removed from Wishlist' : 'Added to Wishlist',
        variant: wasWishlisted ? 'default' : 'success',
      });

      // Dispatch custom event to notify other components (like wishlist page)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('wishlist-changed', {
            detail: { id: normalizedPropertyId, removed: wasWishlisted },
          }),
        );
      }
    } catch (error: any) {
      // Revert optimistic update
      setPropertyInteractionState(normalizedPropertyId, {
        isWishlisted: wasWishlisted,
      });
      toast({
        title: 'Error',
        description: error.message || 'Failed to update wishlist',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [normalizedPropertyId, isWishlisted, isAuthenticated, accessToken]);

  return {
    isLiked,
    isWishlisted,
    isLoading,
    toggleLike,
    toggleWishlist,
    isAuthenticated,
  };
}

/**
 * Fetch user's wishlist from the backend
 * @param accessToken - The user's access token
 * @param signal - Optional abort signal
 */
export async function fetchUserWishlist(
  accessToken: string,
  signal?: AbortSignal,
): Promise<string[]> {
  try {
    const response = await apiFetch('/properties/wishlist', accessToken, {
      signal,
    });

    if (!response.ok) return [];

    const data = await response.json();
    if (Array.isArray(data.properties)) {
      return data.properties.map((item: { id?: string }) => String(item.id));
    }
    return [];
  } catch {
    return [];
  }
}

/**
 * Fetch user's liked properties from the backend
 * @param accessToken - The user's access token
 * @param signal - Optional abort signal
 */
export async function fetchUserLikes(
  accessToken: string,
  signal?: AbortSignal,
): Promise<string[]> {
  try {
    const response = await apiFetch('/properties/likes', accessToken, {
      signal,
    });

    if (!response.ok) return [];

    const data = await response.json();
    if (Array.isArray(data.properties)) {
      return data.properties.map((item: { id?: string }) => String(item.id));
    }
    return [];
  } catch {
    return [];
  }
}

/**
 * Hook for managing multiple properties' like/wishlist status (for table/list views)
 * @param properties - Array of properties with optional is_liked and is_wishlisted fields
 */
export function useMultiplePropertyActions(properties?: PropertyWithStatus[]) {
  const { data: session, status } = useSession();
  const [isLoading, setIsLoading] = useState(false);

  const isAuthenticated = status === 'authenticated';
  const accessToken = session?.user?.accessToken;
  const ownerKey = session?.user?.email || accessToken || 'anonymous';

  usePropertyInteractionOwner(ownerKey);
  useSyncExternalStore(
    subscribeToPropertyInteractions,
    getPropertyInteractionSnapshot,
    getPropertyInteractionSnapshot,
  );

  useEffect(() => {
    if (!properties?.length) return;

    let changed = false;

    properties.forEach((prop) => {
      changed =
        seedPropertyInteractionState(String(prop.id), {
          isLiked: prop.is_liked,
          isWishlisted: prop.is_wishlisted,
        }) || changed;
    });

    if (changed) {
      emitPropertyInteractionChange();
    }
  }, [properties]);

  const { likedItems, wishlistedItems } = useMemo(() => {
    const nextLikedItems = new Set<string>();
    const nextWishlistedItems = new Set<string>();

    properties?.forEach((prop) => {
      const id = String(prop.id);
      const state = resolvePropertyInteractionState(id, {
        isLiked: prop.is_liked,
        isWishlisted: prop.is_wishlisted,
      });

      if (state.isLiked) nextLikedItems.add(id);
      if (state.isWishlisted) nextWishlistedItems.add(id);
    });

    return {
      likedItems: nextLikedItems,
      wishlistedItems: nextWishlistedItems,
    };
  }, [properties, propertyInteractionVersion]);

  const toggleLike = useCallback(
    async (propertyId: string) => {
      if (!isAuthenticated || !accessToken) {
        toast({
          title: 'Please log in',
          description: 'You need to be logged in to like properties',
          variant: 'destructive',
        });
        return;
      }

      const wasLiked = likedItems.has(propertyId);
      setIsLoading(true);

      // Optimistic update
      setPropertyInteractionState(propertyId, { isLiked: !wasLiked });

      try {
        const response = await apiFetch(
          `/properties/${encodeURIComponent(propertyId)}/like`,
          accessToken,
          {
            method: wasLiked ? 'DELETE' : 'POST',
          },
        );

        if (!response.ok) {
          // Revert
          setPropertyInteractionState(propertyId, { isLiked: wasLiked });
          toast({
            title: 'Error',
            description: 'Failed to update like status',
            variant: 'destructive',
          });
          return;
        }

        toast({
          title: wasLiked ? 'Removed from likes' : 'Added to likes',
          variant: 'default',
        });
      } catch {
        // Revert
        setPropertyInteractionState(propertyId, { isLiked: wasLiked });
        toast({
          title: 'Error',
          description: 'Failed to update like status',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, accessToken, likedItems],
  );

  const toggleWishlist = useCallback(
    async (propertyId: string) => {
      if (!isAuthenticated || !accessToken) {
        toast({
          title: 'Please log in',
          description: 'You need to be logged in to add properties to wishlist',
          variant: 'destructive',
        });
        return;
      }

      const wasWishlisted = wishlistedItems.has(propertyId);
      setIsLoading(true);

      // Optimistic update
      setPropertyInteractionState(propertyId, {
        isWishlisted: !wasWishlisted,
      });

      try {
        const response = await apiFetch(
          `/properties/${encodeURIComponent(propertyId)}/wishlist`,
          accessToken,
          {
            method: wasWishlisted ? 'DELETE' : 'POST',
          },
        );

        if (!response.ok) {
          // Revert
          setPropertyInteractionState(propertyId, {
            isWishlisted: wasWishlisted,
          });
          toast({
            title: 'Error',
            description: 'Failed to update wishlist',
            variant: 'destructive',
          });
          return;
        }

        toast({
          title: wasWishlisted ? 'Removed from Wishlist' : 'Added to Wishlist',
          variant: wasWishlisted ? 'default' : 'success',
        });

        // Dispatch custom event
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('wishlist-changed', {
              detail: { id: propertyId, removed: wasWishlisted },
            }),
          );
        }
      } catch {
        // Revert
        setPropertyInteractionState(propertyId, {
          isWishlisted: wasWishlisted,
        });
        toast({
          title: 'Error',
          description: 'Failed to update wishlist',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, accessToken, wishlistedItems],
  );

  return {
    likedItems,
    wishlistedItems,
    isLoading,
    toggleLike,
    toggleWishlist,
    isAuthenticated,
  };
}
